import {
  ChartDisplayData, fetchPieChartData, PieChartDataItem,
  PieChartHookOptions,
  PieChartHookState,
  PieChartParams
} from '@/api/dashboard-api'
import { useCallback, useEffect, useState } from 'react'

/**
* Custom hook for fetching and managing pie chart data
* @param params - Chart API parameters
* @param options - Hook configuration options
* @returns Chart data state and controls
*/
export const usePieChart = (
params: PieChartParams | null,
options: PieChartHookOptions = {}
): PieChartHookState => {
const {
  enabled = true,
  refetchInterval,
  onSuccess,
  onError
} = options

const [data, setData] = useState<ChartDisplayData[]>([])
const [rawData, setRawData] = useState<PieChartDataItem[]>([])
const [loading, setLoading] = useState<boolean>(false)
const [error, setError] = useState<string | null>(null)
const [hasTimedOut, setHasTimedOut] = useState<boolean>(false)
const [hasFetched, setHasFetched] = useState<boolean>(false)

/**
 * Transform raw API data to display format
 */
const transformData = useCallback((rawItems: PieChartDataItem[]): ChartDisplayData[] => {
  return rawItems.map(item => ({
    label: item.type_of_concern,
    value: item.count,
    category: item.category,
    typeOfConcern: item.type_of_concern
  }))
}, [])

/**
 * Fetch chart data from API
 */
const fetchData = useCallback(async () => {
  if (!enabled) return;

  if (!params?.userId || !params?.category) {
    console.log('Missing required params:', { userId: params?.userId, category: params?.category });
    return;
  }
  
  if (loading) return;

  setLoading(true);
  setError(null);
  setHasTimedOut(false);

  const timeoutId = setTimeout(() => {
    setHasTimedOut(true);
    setLoading(false);
  }, 10000);

  try {
    const response = await fetchPieChartData(params);
    clearTimeout(timeoutId);

    if (response.success && response.data) {
      const transformedData = transformData(response.data);
      setRawData(response.data);
      setData(transformedData);
      setHasTimedOut(false);
      onSuccess?.(transformedData);
    } else {
      const errorMessage = response.message || 'Failed to fetch chart data';
      setError(errorMessage);
      onError?.(new Error(errorMessage));
    }
  } catch (err) {
    clearTimeout(timeoutId);
    const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
    setError(errorMessage);
    onError?.(err instanceof Error ? err : new Error(errorMessage));
  } finally {
    setLoading(false);
  }
}, [params?.userId, params?.roleName, params?.year, params?.category, params?.sinceDate, params?.untilDate, enabled, transformData, onSuccess, onError]);

/**
 * Refetch data manually
 */
const refetch = useCallback(async () => {
  setHasFetched(false)
  await fetchData()
}, [fetchData])

// Initial fetch when hook mounts or when parameters change
useEffect(() => {
  if (!hasFetched && params?.userId && params?.category) {
    setHasFetched(true)
    fetchData()
  }
}, [fetchData, params?.userId, params?.roleName, params?.year, params?.category, params?.sinceDate, params?.untilDate, hasFetched])

// Reset hasFetched when parameters change to allow new fetch
useEffect(() => {
  setHasFetched(false)
}, [params?.userId, params?.roleName, params?.year, params?.category, params?.sinceDate, params?.untilDate])

// Auto-refetch interval
useEffect(() => {
  if (!refetchInterval || !enabled) return

  const interval = setInterval(() => {
    fetchData()
  }, refetchInterval)

  return () => clearInterval(interval)
}, [refetchInterval, enabled, fetchData])

return {
  data,
  rawData,
  loading,
  error,
  hasTimedOut,
  refetch
}
}

export default usePieChart